import { poolVergleicher, type MitWunsch } from './pools'
import type { Termin } from './planung'

/**
 * **Eine Rangfolge statt zweier Listen** ([Z-06](../../../docs/phasen/zusatz-06-nehmen-und-starten.md)).
 *
 * ## Das Problem, das diese Datei auflöst
 *
 * „Mein Tag" beantwortete die Frage *was mache ich als Nächstes* an **zwei** Stellen mit
 * **zwei** Regeln: links eine flache Liste der eigenen Aufgaben ohne jede Sortierung, rechts
 * der Vorrat mit Pool-Reihenfolge, Zeitfenster, Priorität und ★. Ausgerechnet die größere
 * Hälfte – das Eigene – hatte keine Ordnung.
 *
 * Nils, 24.08.2026: *„Ich denke es ist für einen Entwickler noch sehr schwierig zu
 * unterscheiden, wann er Aufgaben ziehen soll und wann er auf seinen Vorrat zugreift."*
 *
 * Hier steht deshalb die eine Reihenfolge, in der beides zusammenläuft.
 *
 * ## Die zwei Handgriffe, die dahinterstehen
 *
 * | | **Nehmen** | **Starten** |
 * |---|---|---|
 * | heißt | „gehört mir" | „ich mache das jetzt" |
 * | Status | bleibt | `in_arbeit` |
 * | WIP-Limit | gilt **nicht** | gilt |
 *
 * Füllen darf man immer, anfangen nur dreimal. Deshalb hängt das Limit an `darfStarten` und
 * nicht mehr am Nehmen – ein voller Fokus ist kein Grund, sich nicht versorgen zu dürfen.
 */

/**
 * **Was eine Zeile für den Betrachter ist** – und damit, welcher Knopf an ihr steht.
 *
 * `bei_dir` heißt: **Work** führt mich als Bearbeiter (`work_assignee_id`), nicht die Quelle.
 * Der Unterschied ist der Kern des ganzen Pakets: `assignee_id` aus GitLab oder NetCon sagt
 * über Work **nichts**, und weil die Anzeige beides als „Bearbeiter" zusammenfallen ließ,
 * stand der eigene Name an Aufgaben, die man sich trotzdem noch ziehen konnte (gemessen am
 * 24.08.2026: 44 von 69 ziehbaren Aufgaben).
 */
export type Marke = 'bei_dir' | 'vorgemerkt' | 'herrenlos'

/** Die drei Blöcke der Rangfolge, in genau dieser Reihenfolge. */
export type Gruppe = 'sofort' | 'bei_dir' | 'vorrat'

const GRUPPEN_RANG: Record<Gruppe, number> = { sofort: 0, bei_dir: 1, vorrat: 2 }

/**
 * **Sofort zuerst, dann Eigenes, dann der Vorrat.**
 *
 * Die drei Stufen und ihre Begründung:
 *
 * 1. **🔥 Sofort** geht immer vor ([E-209](../../../docs/ENTSCHEIDUNGEN.md)) – auch vor dem,
 *    was mir schon gehört. Eine Störung wartet nicht auf meine Planung.
 * 2. **Bei dir** vor dem Vorrat, aus demselben Grund, aus dem ★ innerhalb eines Pools nach
 *    vorn sortiert: hier hat jemand – der Leiter oder ich selbst – bereits entschieden, dass
 *    diese Arbeit zu mir gehört. Eine herrenlose Aufgabe ist eine, die **niemand geplant
 *    hat**; wer Ungeplantes vor Geplantes stellt, dreht die Planung um.
 * 3. **Vorrat** in seiner eigenen Pool-Reihenfolge.
 *
 * **Sortiert wird nur nach der Gruppe**, und das ist Absicht: *innerhalb* einer Gruppe steht
 * die Reihenfolge schon fest, bevor diese Funktion sie sieht (`poolVergleicher`, je Pool
 * angewandt). `Array.prototype.sort` ist seit ES2019 stabil – die feinere Ordnung überlebt
 * also. Sie hier ein zweites Mal zu berechnen hieße, zwei Auslegungen von „was ist dringender"
 * zu führen, und die erste, die auseinanderläuft, merkt niemand.
 */
export function nachGruppe<T extends { gruppe: Gruppe }>(zeilen: readonly T[]): T[] {
  return [...zeilen].sort((a, b) => GRUPPEN_RANG[a.gruppe] - GRUPPEN_RANG[b.gruppe])
}

/**
 * In welchen Block eine Zeile gehört.
 *
 * `durchbrichtWip` statt der Pool-Kennung `sofort`: die Regel hängt an der Eigenschaft, die
 * einen Pool zum Sofort-Vorrat macht, nicht an einem Namen – ein zweiter solcher Pool erbt
 * sie. Dieselbe Überlegung wie in `sofortSichtbarFuer`.
 *
 * **Was mir gehört, liegt in keinem Pool** (`nehmen` und `zuweisen` leeren `task_pool`), kann
 * also nie im Sofort-Block landen. Der Fall ist trotzdem sauber beantwortet, statt sich auf
 * die Invariante zu verlassen: Sofort schlägt auch den Besitz.
 */
export function gruppeVon(zeile: { gehoertMir: boolean; durchbrichtWip: boolean }): Gruppe {
  if (zeile.durchbrichtWip) return 'sofort'
  return zeile.gehoertMir ? 'bei_dir' : 'vorrat'
}

/**
 * Welche Marke an der Zeile steht.
 *
 * Ohne Sitzung ist niemand „ich" – dann ist nichts vorgemerkt und nichts gehört mir. Dieselbe
 * Auslegung wie in `fuerAndereVorgemerkt`: wer nicht angemeldet ist, ist niemand Bestimmtes.
 */
export function markeVon(
  zeile: { gehoertMir: boolean; wunschIds?: readonly string[] },
  ichId: string | null | undefined,
): Marke {
  if (zeile.gehoertMir) return 'bei_dir'
  if (ichId && (zeile.wunschIds?.includes(ichId) ?? false)) return 'vorgemerkt'
  return 'herrenlos'
}

/**
 * **Wie viel Genommenes ist zu viel?** (Nils, 24.08.2026 – *„Reicht dir das, oder willst du
 * eine echte Grenze?"* → keine harte Grenze.)
 *
 * ## Warum keine Sperre
 *
 * Weil eine Sperre dem zuwiderliefe, worauf [Z-04](../../../docs/phasen/zusatz-02-mein-tag.md)
 * gerade hinarbeitet: Aufgaben sollen Leuten **vorgesehen** werden, 55 von 76 ziehbaren haben
 * heute niemanden. Ein Deckel auf „gehört mir" würde genau die Bewegung ausbremsen, die
 * gewünscht ist.
 *
 * ## Warum trotzdem eine Zahl
 *
 * Ohne WIP-Sperre am Nehmen kann sich jemand den halben Vorrat an sich ziehen, und der
 * gemeinsame Vorrat läuft leer, ohne dass es jemandem auffällt. Der Hinweis ist die leiseste
 * Form, das sichtbar zu machen: er sagt, was ist, und verbietet nichts.
 *
 * Die Schwelle ist das **Doppelte** des WIP-Limits – bei drei Fokus-Plätzen also ab sieben.
 * Eine glatte Zahl wäre bei einem Limit von 1 oder 5 sinnlos; so wächst sie mit der Person.
 *
 * Gibt `null` zurück, wenn es nichts zu sagen gibt – die Oberfläche zeigt dann nichts, statt
 * eine beruhigende Zeile zu drucken, die niemand liest.
 */
export function zuvielGenommen(
  genommen: number,
  laufend: number,
  wipLimit: number,
  /**
   * **Woran „laufen" gemessen ist** – „in deinen geteilten Welten" oder „in <Welt>"
   * ({@link wipBereichKurz}). Ohne Angabe bleibt der Satz wie vorher.
   *
   * Nils, 02.10.2026 bei der Abnahme von Z-19: Hier stand *„und 0 laufen"*, während drei
   * Aufgaben sichtbar liefen – persönliche Welten zählen nicht mit
   * ([E-238](../../../docs/ENTSCHEIDUNGEN.md#e-238)). Die Zahl war richtig und las sich falsch.
   */
  bereich?: string | null,
): string | null {
  if (wipLimit <= 0 || genommen <= wipLimit * 2) return null
  const laufenText = bereich ? `${laufend} ${bereich} laufen` : `${laufend} laufen`
  return (
    `Du hast ${genommen} Aufgaben genommen und ${laufenText}. ` +
    'Was du nicht bald anfängst, fehlt den anderen im Vorrat – gib zurück, was liegen bleibt.'
  )
}

/**
 * **Eine Rangfolge für alle Ansichten** – Vorlauf, Kalender, „Gefährdet", Circle und Daily
 * ([Z-20](../../../docs/phasen/zusatz-20-mein-tag-markieren.md), 06.10.2026).
 *
 * Nils, 06.10.2026, mit einem Bild des Kalenders: *„Ich glaube wir brauchen ein einheitliches
 * Vorlauf/Prio System. […] Wir sollten das an allen Stellen immer gleich haben."*
 *
 * Bis hierher gab es **vier** Regeln: der Vorlauf (`poolVergleicher`, mit wirksamem Termin), der
 * Kalender und „Gefährdet" (nur die Fälligkeit), das Rad (`nachFristPrioAlter`, nur die
 * Fälligkeit) und das Daily (eine eigene Kopie). Folge: „Features und Review" mit Zeitfenster
 * „Jetzt" stand im Vorlauf auf Platz 1 und galt im Kalender als Aufgabe ohne Frist.
 *
 * Jetzt gibt es **eine**: die des Vorlaufs. Sie ist die ausgearbeitetste und mehrfach von Nils
 * bestätigt – was brennt (überfällig oder heute) → ★ → Dringlichkeitsstufe → Priorität → Termin
 * → Alter. Diese Funktion macht sie nur für Zeilen aufrufbar, die **keine** Pool-Zeilen sind.
 *
 * `termin` ist der **wirksame** Termin (`wirksamerTermin`: Fälligkeit → Meilenstein → Zieltermin
 * → Zeitfenster). Fehlt er, gilt die Fälligkeit – damit bleiben Aufrufer, die nur sie kennen,
 * richtig, statt still auf „ohne Termin" zu fallen.
 */
export type Rangzeile = {
  termin?: Termin
  dueAt?: string | Date | null
  priority?: string | null
  sourceCreatedAt?: string | Date | null
  createdAt?: string | Date | null
  updatedAt?: string | Date | null
  wunschIds?: readonly string[]
}

export function arbeitsRang(
  ichId: string | null | undefined,
  heute: Date,
): (a: Rangzeile, b: Rangzeile) => number {
  const vergleich = poolVergleicher(ichId, heute)
  /** Je Zeile **ein** Objekt – `poolVergleicher` merkt sich die Stufe über die Objektkennung. */
  const formen = new WeakMap<Rangzeile, MitWunsch>()
  const form = (z: Rangzeile): MitWunsch => {
    let f = formen.get(z)
    if (!f) {
      f = {
        termin:
          z.termin !== undefined
            ? z.termin
            : z.dueAt
              ? { datum: new Date(z.dueAt), quelle: 'faellig' }
              : null,
        priority: z.priority ?? null,
        sourceCreatedAt: z.sourceCreatedAt ?? null,
        /** Ohne Anlagedatum zählt die letzte Änderung – ohne beides gilt die Zeile als neu. */
        createdAt: z.createdAt ?? z.updatedAt ?? heute,
        ...(z.wunschIds ? { wunschIds: z.wunschIds } : {}),
      }
      formen.set(z, f)
    }
    return f
  }
  return (a, b) => vergleich(form(a), form(b))
}
